import { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { BY_Z, BY_SYMBOL, shellOccupancy } from './elements';
import { useAnimationFrame } from './useAnimationFrame';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from './vizShared';

// BohrAtom — the instructor's atoms-&-orbits example. Nucleus (protons red,
// neutrons grey) with electrons ORBITING in shells (capacities 2,8,8,18…).
// ± controls for protons / neutrons / electrons; derives symbol, name, mass
// number and charge live. config: { protons, neutrons?, electrons?, title? }.
// Resilient: defaults to a neutral carbon atom; clamps to valid ranges.

export interface BohrAtomConfig {
  title?: string;
  protons?: number;
  neutrons?: number;
  electrons?: number;
}

interface Props {
  config?: BohrAtomConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const SIZE = 280;
const C = SIZE / 2; // center
const NUCLEUS_R = 30;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export default function BohrAtom({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);

  // Target (configured) values — the resting state the demo ramps toward.
  const targetProtons = clamp(config.protons ?? 6, 1, 118);
  const targetNeutrons = clamp(config.neutrons ?? config.protons ?? 6, 0, 200);
  const targetElectrons = clamp(config.electrons ?? config.protons ?? 6, 0, 118);

  // Whether the assembly demo should run: only when motion allowed + no cue.
  const demoEnabled = !reducedMotion && !cueOn;

  const [protons, setProtons] = useState(() => (demoEnabled ? 0 : targetProtons));
  const [neutrons, setNeutrons] = useState(() => (demoEnabled ? 0 : targetNeutrons));
  const [electrons, setElectrons] = useState(() => (demoEnabled ? 0 : targetElectrons));
  const [angle, setAngle] = useState(0);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const demoActiveRef = useRef(demoEnabled);

  useAnimationFrame((dt) => setAngle((a) => a + dt * 0.8));

  // --- Self-demo: ramp 0 → target over ~1.5s, then show "Now you try". -----
  useEffect(() => {
    if (!demoEnabled) {
      setProtons(targetProtons);
      setNeutrons(targetNeutrons);
      setElectrons(targetElectrons);
      return;
    }
    demoActiveRef.current = true;
    const DURATION = 1500;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      if (!demoActiveRef.current) return;
      const t = clamp((now - start) / DURATION, 0, 1);
      // ease-out
      const e = 1 - Math.pow(1 - t, 3);
      setProtons(Math.round(targetProtons * e));
      setNeutrons(Math.round(targetNeutrons * e));
      setElectrons(Math.round(targetElectrons * e));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        demoActiveRef.current = false;
        setShowTry(true);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      demoActiveRef.current = false;
      cancelAnimationFrame(raf);
    };
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Cue: drive the assembly ramp via progress/step, or set element by symbol.
  // Stops once the user takes manual control via the ± steppers, otherwise every
  // audio tick would re-ramp and stomp their edits (the "steppers reset" bug).
  useEffect(() => {
    if (!cue) return;
    if (interactedRef.current) return;
    demoActiveRef.current = false; // cue supersedes demo
    setShowTry(false);

    // Element-by-symbol highlight.
    const tokens = cueHighlights(cue);
    for (const tok of tokens) {
      const match = Object.values(BY_SYMBOL).find(
        (el) => el.symbol.toLowerCase() === tok,
      );
      if (match) {
        setProtons(clamp(match.z, 1, 118));
        setNeutrons(clamp(Math.round(match.mass) - match.z, 0, 200));
        setElectrons(clamp(match.z, 0, 118));
        return;
      }
    }

    // Progress / step animate the build-up ONLY while playing; at rest (paused —
    // e.g. a student landing on the slide) show the FULL configured atom, never an
    // empty "Unknown / 0 protons" shell.
    let frac: number;
    if (cue.play === true && typeof cue.progress === 'number') frac = clamp(cue.progress, 0, 1);
    else if (cue.play === true && typeof cue.step === 'number') frac = clamp(cue.step / 3, 0, 1);
    else frac = 1;
    setProtons(Math.round(targetProtons * frac));
    setNeutrons(Math.round(targetNeutrons * frac));
    setElectrons(Math.round(targetElectrons * frac));
  }, [cue, targetProtons, targetNeutrons, targetElectrons]);

  const cancelDemo = () => {
    if (demoActiveRef.current) {
      demoActiveRef.current = false;
      setProtons(targetProtons);
      setNeutrons(targetNeutrons);
      setElectrons(targetElectrons);
    }
  };

  const markInteracted = () => {
    cancelDemo();
    setShowTry(false);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'bohr_atom', protons, neutrons, electrons });
    }
  };

  const adjust = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
    min: number,
    max: number,
  ) => {
    markInteracted();
    setter((v) => clamp(v + delta, min, max));
  };

  const el = BY_Z[protons];
  const massNumber = protons + neutrons;
  const charge = protons - electrons;
  const chargeLabel = charge === 0 ? 'neutral' : charge > 0 ? `+${charge}` : `${charge}`;
  const shells = shellOccupancy(electrons);

  // Nucleon dots packed in a rough disc inside the nucleus.
  const nucleons: Array<{ x: number; y: number; type: 'p' | 'n' }> = [];
  {
    const total = protons + neutrons;
    const order: Array<'p' | 'n'> = [];
    for (let i = 0; i < total; i++) order.push(i % 2 === 0 ? 'p' : 'n');
    let pLeft = protons;
    let nLeft = neutrons;
    const typed = order.map((t) => {
      if (t === 'p' && pLeft > 0) { pLeft--; return 'p'; }
      if (t === 'n' && nLeft > 0) { nLeft--; return 'n'; }
      if (pLeft > 0) { pLeft--; return 'p'; }
      nLeft--; return 'n';
    });
    // Phyllotaxis spiral packing.
    const golden = Math.PI * (3 - Math.sqrt(5));
    typed.forEach((t, i) => {
      const r = (NUCLEUS_R - 5) * Math.sqrt(i / Math.max(1, total));
      const a = i * golden;
      nucleons.push({ x: C + r * Math.cos(a), y: C + r * Math.sin(a), type: t });
    });
  }

  return (
    <VizCard
      eyebrow="Bohr model"
      title={config.title}
      reducedMotion={reducedMotion}
      hint="Adjust protons, neutrons and electrons · ± steppers update the atom live"
    >
      <div className="flex w-full flex-col gap-3 lg:flex-row">
        <div className="relative flex-shrink-0">
          <NowYouTry show={showTry} />
          <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            className="w-full max-w-[280px]"
            role="img"
            aria-label={`Bohr model: ${el?.name ?? 'atom'}`}
          >
            {/* Shell rings + electrons */}
            {shells.map((count, s) => {
              const radius = NUCLEUS_R + 22 + s * 26;
              return (
                <g key={s}>
                  <circle cx={C} cy={C} r={radius} fill="none" stroke="var(--ws-border)" strokeWidth="1" />
                  {Array.from({ length: count }).map((_, i) => {
                    // Each shell rotates a bit slower the further out it is.
                    const base = (i / count) * Math.PI * 2;
                    const a = base + angle / (1 + s * 0.4);
                    const ex = C + radius * Math.cos(a);
                    const ey = C + radius * Math.sin(a);
                    return <circle key={i} cx={ex} cy={ey} r="5" fill="#F2AF3F" stroke="#fff" strokeWidth="1" />;
                  })}
                </g>
              );
            })}

            {/* Nucleus */}
            <circle cx={C} cy={C} r={NUCLEUS_R} fill="var(--ws-surface)" stroke="var(--ws-border-strong)" strokeWidth="1" />
            {nucleons.map((n, i) => (
              <circle
                key={i}
                cx={n.x}
                cy={n.y}
                r="4.5"
                fill={n.type === 'p' ? '#E0564B' : '#3B82F6'}
                stroke="rgba(0,0,0,0.25)"
                strokeWidth="0.5"
              />
            ))}
          </svg>
          {/* Legend: protons / neutrons / electrons in three distinct colors */}
          <div className="mt-1 flex items-center justify-center gap-4 text-xs text-ws-ink-2">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#E0564B' }} />Protons (+)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#3B82F6' }} />Neutrons (0)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#F2AF3F' }} />Electrons (−)</span>
          </div>
        </div>

        {/* Readout + controls */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <span
              className="text-2xl font-bold text-ws-ink transition-all duration-300"
              style={{ transition: reducedMotion ? 'none' : 'opacity 300ms ease-out' }}
            >
              {el?.symbol ?? '?'}
            </span>
            <span className="text-sm text-ws-ink-2">{el?.name ?? 'Unknown'}</span>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ws-ink-2">
            <div><dt className="inline text-ws-ink-3">Atomic no. (Z): </dt><dd className="inline font-medium tabular-nums">{protons}</dd></div>
            <div><dt className="inline text-ws-ink-3">Mass no. (A): </dt><dd className="inline font-medium tabular-nums">{massNumber}</dd></div>
            <div>
              <dt className="inline text-ws-ink-3">Charge: </dt>
              <dd
                className={`inline font-medium tabular-nums transition-colors duration-300 ${charge === 0 ? '' : charge > 0 ? 'text-ws-danger' : 'text-ws-accent'}`}
              >
                {chargeLabel}
              </dd>
            </div>
            <div><dt className="inline text-ws-ink-3">Config: </dt><dd className="inline font-medium tabular-nums">{shells.join('-') || '0'}</dd></div>
          </dl>

          <div className="flex flex-col gap-2">
            <Stepper label="Protons" color="#E0564B" value={protons} onDec={() => adjust(setProtons, -1, 1, 118)} onInc={() => adjust(setProtons, 1, 1, 118)} />
            <Stepper label="Neutrons" color="#3B82F6" value={neutrons} onDec={() => adjust(setNeutrons, -1, 0, 200)} onInc={() => adjust(setNeutrons, 1, 0, 200)} />
            <Stepper label="Electrons" color="#F2AF3F" value={electrons} onDec={() => adjust(setElectrons, -1, 0, 118)} onInc={() => adjust(setElectrons, 1, 0, 118)} />
          </div>
        </div>
      </div>
    </VizCard>
  );
}

function Stepper({
  label,
  color,
  value,
  onDec,
  onInc,
}: {
  label: string;
  color: string;
  value: number;
  onDec: () => void;
  onInc: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="w-20 text-xs text-ws-ink-2">{label}</span>
      <div className="flex items-center overflow-hidden rounded-sm border border-ws-border">
        <button type="button" onClick={onDec} className={`px-2 py-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 ${VIZ_FOCUS}`} aria-label={`Decrease ${label}`}>
          <Minus size={13} />
        </button>
        <span className="w-9 text-center text-sm font-semibold text-ws-ink tabular-nums">{value}</span>
        <button type="button" onClick={onInc} className={`px-2 py-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 ${VIZ_FOCUS}`} aria-label={`Increase ${label}`}>
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}
